import React, { useMemo } from 'react';
import { useProductOptions, useParsedMetafields } from '../../hooks';
import { flattenConnection } from '../../utilities';
import { ProductContext } from './context';
import { ProductProviderFragment as Fragment } from '../../graphql/graphql-constants';
/**
 * The `ProductProvider` component sets up a context with product details. Descendents of
 * this component can use the `useProduct` hook and the related `ProductX` or `SelectedVariantX` hooks.
 */
export function ProductProvider({ children, product, initialVariantId, }) {
    const { variants, options, selectedVariant, setSelectedVariant, selectedOptions, setSelectedOption, setSelectedOptions, isOptionInStock, selectedSellingPlan, selectedSellingPlanAllocation, setSelectedSellingPlan, sellingPlanGroups, } = useProductOptions({
        variants: product.variants,
        initialVariantId: initialVariantId,
    });
    const metafields = useParsedMetafields(product.metafields);
    const providerValue = useMemo(() => {
        return {
            ...product,
            metafields,
            metafieldsConnection: product.metafields,
            media: product.media ? flattenConnection(product.media) : undefined,
            mediaConnection: product.media,
            variants: variants,
            variantsConnection: product.variants,
            images: product.images ? flattenConnection(product.images) : undefined,
            imagesConnection: product.images,
            collections: product.collections
                ? flattenConnection(product.collections)
                : undefined,
            collectionsConnection: product.collections,
            options,
            selectedVariant,
            setSelectedVariant,
            selectedOptions,
            setSelectedOption,
            setSelectedOptions,
            isOptionInStock,
            selectedSellingPlan,
            selectedSellingPlanAllocation,
            setSelectedSellingPlan,
            sellingPlanGroups,
        };
    }, [
        isOptionInStock,
        metafields,
        options,
        product,
        selectedOptions,
        selectedSellingPlan,
        selectedSellingPlanAllocation,
        selectedVariant,
        sellingPlanGroups,
        setSelectedOption,
        setSelectedOptions,
        setSelectedSellingPlan,
        setSelectedVariant,
        variants,
    ]);
    return (React.createElement(ProductContext.Provider, { value: providerValue }, children));
}
ProductProvider.Fragment = Fragment;
export const ProductProviderFragment = Fragment;
